import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import { loginUser, isTokenExpired } from '../utils/authUtils';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ClipLoader from 'react-spinners/ClipLoader';

const Base_Url = process.env.REACT_APP_API_BASE_URL;
const Products = process.env.REACT_APP_API_PRODUCTS;
const Brands = process.env.REACT_APP_API_BRANDS;
const limit = process.env.REACT_APP_API_LIMIT;
const Category_list = process.env.REACT_APP_API_CATEGORY;
const Membershipsignup = process.env.REACT_APP_LINK_SIGNUP


const Product = () => {
    const { id } = useParams();
    const location = useLocation();
    const filterLabel = location.state?.filterLabel || (id ? 'Brands' : 'Categories');
    const fromHeaderSearch = location.state?.fromHeaderSearch || false;
    const navigate = useNavigate();

    // Product data and UI states
    const [product, setProduct] = useState({ products: [] });
    const [brands, setBrands] = useState([]);
    const [brandsFromApi, setBrandsFromApi] = useState([]); // Brands from separate brands API
    const [categories, setCategories] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [brandsLoading, setBrandsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [openFilterType, setOpenFilterType] = useState('');
    const [isFilterOffcanvasOpen, setIsFilterOffcanvasOpen] = useState(false);
    const currentBrandName = brands.find((b) => b._id === id)?.name;
    const [suggestions, setSuggestions] = useState({ brands: [], categories: [], products: [] });
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showAllBrands, setShowAllBrands] = useState(false);
    const visibleBrands = showAllBrands ? brands : brands.slice(0, 10);
    const [endCursor, setEndCursor] = useState(null);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);


    // Pagination
    const [brandPage, setBrandPage] = useState(1);
    const [hasMoreBrands, setHasMoreBrands] = useState(true);

    // Filters
    const [selectedBrandIds, setSelectedBrandIds] = useState(id ? [id] : []);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(
        location.state?.categoryId ? [location.state.categoryId] : []
    );
    const [selectedPriceRange, setSelectedPriceRange] = useState('');
    const [sortBy, setSortBy] = useState('default');

    // Search
    const initialSearch = location.state?.searchTerm?.length >= 2 ? location.state.searchTerm : '';
    const [searchInput, setSearchInput] = useState(initialSearch);
    const [tempSearchInput, setTempSearchInput] = useState(initialSearch);


    // UI
    const [activeTab, setActiveTab] = useState('grid');
    const [openSubcategoryIndexes, setOpenSubcategoryIndexes] = useState({});

    // Refs
    const suggestionRef = useRef();
    const swiperRef = useRef(null);
    const isLoggedIn = localStorage.getItem('userid');

    // Static default brand data (fallback when API doesn't return brands)
    const defaultBrands = [
        { _id: '1', name: 'Black Rifle Coffee', logo: '/assets/img/brands/black_rifle_coffee.png' },
        { _id: '2', name: 'Golf Balls', logo: '/assets/img/brands/golfballs.png' },
        { _id: '3', name: 'NFL', logo: '/assets/img/brands/nfl.png' },
        { _id: '4', name: 'Olipop', logo: '/assets/img/brands/olipop.png' },
        { _id: '5', name: 'Tervis', logo: '/assets/img/brands/tervis.png' },
    ];

    const priceRanges = [
        { value: '', label: 'Any' },
        { value: 'under25', label: 'Under $25' },
        { value: '25to50', label: '$25 to $50' },
        { value: '50to100', label: '$50 to $100' },
        { value: '100to200', label: '$100 to $200' },
    ];

    const sortOptions = [
        { value: 'default', label: 'Default Sorting' },
        { value: 'price_low', label: 'Price: Low to High' },
        { value: 'price_high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newly Added' },
        { value: 'rating', label: 'On Sale' }
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);



    useEffect(() => {
        const body = document.querySelector('body');
        if (isFilterOffcanvasOpen) {
            body.classList.add('body-overlay-open');
        } else {
            body.classList.remove('body-overlay-open');
        }

        return () => {
            body.classList.remove('body-overlay-open');
        };
    }, [isFilterOffcanvasOpen]);


    useEffect(() => {
        if (
            openFilterType === 'offcanvas' &&
            isFilterOffcanvasOpen &&
            filterLabel === 'Brands' &&
            selectedBrandIds.length === 1
        ) {
            fetchCategoriesByBrand(selectedBrandIds[0]);
        }
    }, [openFilterType, isFilterOffcanvasOpen, filterLabel, selectedBrandIds]);



    useEffect(() => {
        if (tempSearchInput === '' && searchInput !== '') {
            setSearchInput('');
        }
    }, [tempSearchInput, searchInput]);

    useEffect(() => {
        const trimmed = tempSearchInput.trim();
        if (trimmed.length < 2) {
            setSuggestions({ brands: [], categories: [], products: [] });
            setShowSuggestions(false);
        }
    }, [tempSearchInput]);


    const loadMoreTriggerRef = useRef();



    useEffect(() => {
        const handleHeaderSearch = (e) => {
            const newSearchTerm = e.detail || '';
            setSearchInput(newSearchTerm);
        };

        window.addEventListener('headerSearch', handleHeaderSearch);

        return () => {
            window.removeEventListener('headerSearch', handleHeaderSearch);
        };
    }, []);

    // Handle brand ID changes from URL (e.g., when clicking brand suggestion while on brand page)
    useEffect(() => {
        if (id) {
            // Update selectedBrandIds when brand ID in URL changes
            setSelectedBrandIds([id]);
            // Clear other filters when switching brands
            setSelectedCategoryIds([]);
            setSearchInput('');
            setTempSearchInput('');
        }
        // Note: Clearing brands when id is removed is handled by category/search useEffects
    }, [id]);

    // Handle category navigation from Header
    useEffect(() => {
        // Watch location.key to detect navigation even when pathname is the same
        if (location.state?.categoryId !== undefined) {
            setSelectedCategoryIds([location.state.categoryId]);
            setSelectedBrandIds([]);
            setSearchInput('');
            setTempSearchInput('');
        }
    }, [location.state?.categoryId, location.key]);

    // Handle search term navigation from Header
    useEffect(() => {
        // Watch location.key to detect navigation even when pathname is the same
        // Only update if searchTerm is explicitly provided in location.state
        if (location.state && 'searchTerm' in location.state) {
            const searchTerm = location.state.searchTerm;
            if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim().length >= 2) {
                const newSearchTerm = searchTerm.trim();
                setSearchInput(newSearchTerm);
                setTempSearchInput(newSearchTerm);
                // Clear other filters when searching
                setSelectedBrandIds([]);
                setSelectedCategoryIds([]);
            } else {
                // Clear search if empty string or invalid
                setSearchInput('');
                setTempSearchInput('');
            }
        }
    }, [location.state?.searchTerm, location.key]);



    const fetchSuggestions = async (query) => {
        if (!query || query.trim().length < 2) {
            setSuggestions({ brands: [], categories: [], products: [] });
            setShowSuggestions(false);
            return;
        }

        try {
            const response = await axios.get(`${Base_Url}/shopping/products/suggestions?q=${query}`);

            const hasSuggestions = (data) =>
                data.brands?.length > 0 || data.categories?.length > 0 || data.products?.length > 0;

            if (response.data.status === 'success' && hasSuggestions(response.data.result)) {
                setSuggestions({
                    brands: response.data.result.brands || [],
                    categories: response.data.result.categories || [],
                    products: response.data.result.products || [],
                });
                setShowSuggestions(true);
            } else {
                setSuggestions({ brands: [], categories: [], products: [] });
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error("Suggestion fetch error:", error);
            setSuggestions({ brands: [], categories: [], products: [] });
            setShowSuggestions(false);
        }
    };


    const fetchCategoriesByBrand = async (brandId) => {
        try {
            const token = localStorage.getItem('jwt_token');
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${Base_Url}/shopping/categories/list?brandId=${brandId}`, { headers });
            if (response.data.status === 'success') {
                setCategories(response.data.result || []);
            }
        } catch (error) {
            console.error('Error fetching categories by brand:', error);
        }
    };

    // BRANDS API - Separate endpoint for brands
    const fetchBrandsForSearch = useCallback(async (searchTerm = '') => {
        try {
            setBrandsLoading(true);
            let url = `${Base_Url}/shopping/products/brands`;
            if (searchTerm && searchTerm.trim().length > 0) {
                url += `?search=${encodeURIComponent(searchTerm.trim())}`;
            }

            const token = localStorage.getItem('jwt_token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(url, { headers });

            // Handle the response structure: { data: { brands: [...] }, performance: {...} }
            const brandsData = response.data?.data?.brands || response.data?.brands || [];
            setBrandsFromApi(Array.isArray(brandsData) ? brandsData : []);
        } catch (error) {
            if (error.response?.status === 401) {
                console.warn('Token invalid. Retrying login for brands...');
                await loginUser();
                return fetchBrandsForSearch(searchTerm);
            }
            console.error('Error fetching brands:', error);
            setBrandsFromApi([]);
        } finally {
            setBrandsLoading(false);
        }
    }, []);

    // CATEGORY API
    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('jwt_token');
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${Base_Url}/${Category_list}/list?status=active`, { headers });
            if (response.data.status === 'success') {
                setCategories(response.data.result || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    // Fetch brands from separate API when needed
    useEffect(() => {
        if (fromHeaderSearch) {
            // Fetch brands when coming from header search
            fetchBrandsForSearch(searchInput);
        } else if (searchInput && searchInput.trim().length >= 2) {
            // Fetch brands when user searches
            fetchBrandsForSearch(searchInput);
        } else {
            // Clear brands when no search
            setBrandsFromApi([]);
        }
    }, [fromHeaderSearch, searchInput, fetchBrandsForSearch]);

    // Initialize Swiper for brand slider
    useEffect(() => {
        if (brandsFromApi.length === 0 || brandsLoading) return;

        // Wait for Swiper to be available and DOM to be ready
        const initSwiper = () => {
            if (typeof window !== 'undefined' && window.Swiper) {
                // Clean up existing Swiper instance if any
                if (swiperRef.current) {
                    swiperRef.current.destroy(true, true);
                    swiperRef.current = null;
                }

                // Use setTimeout to ensure DOM is ready
                setTimeout(() => {
                    const swiperElement = document.querySelector('.tp-product-arrival-active.swiper');
                    if (swiperElement) {
                        swiperRef.current = new window.Swiper('.tp-product-arrival-active.swiper', {
                            loop: true,
                            spaceBetween: 20, // gap between slides
                            pagination: {
                                el: '.swiper-pagination',
                                clickable: true,
                            },
                            navigation: {
                                nextEl: '.tp-arrival-slider-button-next',
                                prevEl: '.tp-arrival-slider-button-prev',
                            },
                            autoplay: {
                                delay: 3000, // time in ms between slides (3 seconds)
                                disableOnInteraction: false, // keep autoplay even after user swipes
                            },
                            // Responsive breakpoints
                            breakpoints: {
                                0: {   // phones
                                    slidesPerView: 1,
                                },
                                576: {   // tablets
                                    slidesPerView: 2,
                                },
                                768: {   // tablets
                                    slidesPerView: 2,
                                },
                                992: {  // desktops
                                    slidesPerView: 3,
                                },
                                1200: {  // large screens
                                    slidesPerView: 4,
                                },
                            },
                        });
                    }
                }, 100);
            } else {
                // If Swiper is not loaded yet, wait a bit and try again
                setTimeout(initSwiper, 100);
            }
        };

        initSwiper();

        // Cleanup function
        return () => {
            if (swiperRef.current) {
                swiperRef.current.destroy(true, true);
                swiperRef.current = null;
            }
        };
    }, [brandsFromApi, brandsLoading]);

    // BRANDS API
    const fetchBrands = useCallback(async (page, categoryIds = [], customLimit = 10) => {
        try {
            const token = localStorage.getItem('jwt_token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const brandlimit = customLimit;

            let url = `${Base_Url}/${Brands}/list?page=${page}&limit=${brandlimit}`;
            if (categoryIds.length > 0) {
                url += `&categoryIds=${categoryIds.join(',')}`;
            }

            const response = await axios.get(url, { headers });

            if (response.data.status === 'success') {
                const newBrands = response.data.result.brands || [];
                if (page === 1) {
                    const sortedBrands = id
                        ? newBrands.sort((a, b) => (a._id === id ? -1 : b._id === id ? 1 : 0))
                        : newBrands;
                    setBrands(sortedBrands);
                } else {
                    setBrands((prev) => {
                        const ids = new Set(prev.map((b) => b._id));
                        const uniqueBrands = newBrands.filter((b) => !ids.has(b._id));
                        return [...prev, ...uniqueBrands];
                    });
                }
                setHasMoreBrands(newBrands.length === brandlimit);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                console.warn('Token invalid. Retrying login...');
                await loginUser();
                return fetchBrands(page, categoryIds);
            }
            console.error('Error fetching brands:', error);
            setError('Failed to load brands');
        }
    }, [id]);

    useEffect(() => {
        setBrandPage(1);
        const brandLimit = showAllBrands ? 500 : 10;
        fetchBrands(1, selectedCategoryIds, brandLimit);
    }, [fetchBrands, selectedCategoryIds, showAllBrands]);

    useEffect(() => {
        if (brandPage > 1) {
            fetchBrands(brandPage, selectedCategoryIds);
        }
    }, [brandPage, fetchBrands, selectedCategoryIds]);

    // PRODUCT API
    const fetchProducts = useCallback(async (token, brandIds, priceRange, sortValue, sortOrder = 'asc', searchTerm = '', category = '', after = null) => {
        try {
            let url = `${Base_Url}/${Products}/list-graphql?first=${limit}&status=active`;
            if (after) url += `&after=${after}`;
            if (brandIds.length > 0) url += `&brandIds=${brandIds.join(',')}`;
            if (priceRange) url += `&priceRange=${priceRange}`;
            if (sortValue) url += `&sortBy=${sortValue}&sortOrder=${sortOrder}`;
            if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
            if (category && Array.isArray(category) && category.length > 0) {
                url += `&categoryIds=${category.join(',')}`;
            }


            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(url, { headers });

            const resData = response.data.data;

            if (resData) {
                const newProducts = resData.edges.map(edge => edge.node);
                setProduct(prev => ({
                    products: after ? [...prev.products, ...newProducts] : newProducts,
                    totalCount: resData.totalCount,
                    pageInfo: resData.pageInfo,
                    remainingItems: resData.remainingItems,
                }));

                setEndCursor(resData.pageInfo.endCursor);
                setHasNextPage(resData.pageInfo.hasNextPage);
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error) {
            if (error.response?.status === 401) {
                console.warn('Token invalid. Retrying login...');
                await loginUser();
                const retryToken = localStorage.getItem('jwt_token');
                return fetchProducts(retryToken, brandIds, priceRange, sortValue, searchTerm, category);
            }
            console.error('Error fetching products:', error);
            setError(error.response?.data?.message || 'Failed to fetch products');
        }
    }, []);

    const fetchProductsWithAuth = useCallback(async (after = null) => {

        if (after && isFetchingMore) {
            console.log('Already fetching more, skipping...');
            return;
        }

        if (after) {
            setIsFetchingMore(true);
        } else {
            setLoading(true);
            setError(null);
        }
        try {
            let token = localStorage.getItem('jwt_token');
            if (!token || isTokenExpired(token)) {
                await loginUser();
                token = localStorage.getItem('jwt_token');
            }


            let apiSortBy = 'createdAt';
            let apiSortOrder = 'asc';

            switch (sortBy) {
                case 'price_low':
                    apiSortBy = 'price.amount';
                    apiSortOrder = 'asc';
                    break;
                case 'price_high':
                    apiSortBy = 'price.amount';
                    apiSortOrder = 'desc';
                    break;
                case 'newest':
                    apiSortBy = 'createdAt';
                    apiSortOrder = 'desc';
                    break;
                case 'rating':
                    apiSortBy = 'rating';
                    apiSortOrder = 'desc';
                    break;
                default:
                    apiSortBy = 'createdAt';
                    apiSortOrder = 'asc';
            }
            await fetchProducts(token, selectedBrandIds, selectedPriceRange, apiSortBy, apiSortOrder, searchInput, selectedCategoryIds, after);

        } catch (err) {
            console.error('Product token auth error:', err);
            setError('Authentication failed. Please reload.');
        } finally {
            if (after) {
                setIsFetchingMore(false); // ✅ fix
            } else {
                setLoading(false);
                setHasLoaded(true);
            }
        }
    }, [fetchProducts, selectedBrandIds, selectedPriceRange, sortBy, searchInput, selectedCategoryIds,]);

    useEffect(() => {
        fetchProductsWithAuth();
    }, [fetchProductsWithAuth, searchInput, selectedCategoryIds]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (
                    entry.isIntersecting &&
                    hasNextPage &&
                    !isFetchingMore &&
                    !loading &&
                    endCursor
                ) {
                    console.log('Loading more products via intersection...');
                    fetchProductsWithAuth(endCursor);
                }
            },
            {
                root: null,
                rootMargin: '100px', // Start loading when element is 100px away from viewport
                threshold: 0.1
            }
        );

        const currentTriggerRef = loadMoreTriggerRef.current;
        if (currentTriggerRef) {
            observer.observe(currentTriggerRef);
        }

        return () => {
            if (currentTriggerRef) {
                observer.unobserve(currentTriggerRef);
            }
        };
    }, [hasNextPage, isFetchingMore, loading, endCursor, fetchProductsWithAuth]);


    useEffect(() => {
        let timeoutId = null;

        const handleScroll = () => {

            if (timeoutId) {
                clearTimeout(timeoutId);
            }


            timeoutId = setTimeout(() => {
                const scrollTop = document.documentElement.scrollTop;
                const windowHeight = window.innerHeight;
                const fullHeight = document.documentElement.scrollHeight;

                const isNearBottom = scrollTop + windowHeight >= fullHeight - 1000;

                if (
                    isNearBottom &&
                    hasNextPage &&
                    !isFetchingMore &&
                    !loading &&
                    endCursor // Ensure we have a cursor for pagination
                ) {
                    console.log('Triggering infinite scroll load more...');
                    fetchProductsWithAuth(endCursor);
                }
            }, 100);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [endCursor, hasNextPage, isFetchingMore, loading, fetchProductsWithAuth]);




    // Handle brand filter toggle
    const handleBrandToggle = (brandId) => {
        setSelectedBrandIds((prev) => {
            const newSelection = prev.includes(brandId)
                ? prev.filter((id) => id !== brandId)
                : [...prev, brandId];

            if (newSelection.length === 1) {
                fetchCategoriesByBrand(newSelection[0]);
            } else {
                fetchCategories();
            }

            return newSelection;
        });
    };


    const handleSingleBrandSelect = (brandId) => {
        setSelectedBrandIds([brandId]);
        fetchCategoriesByBrand(brandId);

    };


    const handleCategoryToggle = (categoryId, children = []) => {
        setSelectedCategoryIds([categoryId]);


    };


    // Handle price range change
    const handlePriceRangeChange = (range) => {
        setSelectedPriceRange(range);

        setIsFilterOffcanvasOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle sort change
    const handleSortChange = (e) => {
        setSortBy(e.target.value);

    };

    // Render star rating
    const renderStarRating = (rating = 0) => {
        return [...Array(5)].map((_, i) => (
            <span key={i}>
                <i className={`fa-solid fa-star${i < rating ? '' : ' text-muted'}`} />
            </span>
        ));
    };

    // Render product grid
    const renderProductGrid = () => {
        if (!loading && hasLoaded && (!product?.products || product.products.length === 0)) {
            return (
                <div className="text-center py-5">
                    <h4>No products found</h4>
                </div>
            );
        }

        return (
            <div className="row infinite-container tp_products_grid">
                {product.products.filter(item => {
                    const src = (item.cloudFrontImageUrl || item.productLogo || '').trim();
                    return src !== '' && !src.toLowerCase().includes('.tif');
                }).map((item, index) => (
                    <div className="col-xl-3 col-md-6 col-sm-6 infinite-item" key={item._id || index}>
                        <div className="tp-product-item-2 mb-40">
                            <div className="tp-product-thumb-2 p-relative z-index-1 fix w-img">
                                <a
                                    href={item.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`View ${item.title}`}
                                >
                                    <img
                                        // loading="lazy"
                                        src={item.cloudFrontImageUrl || item.productLogo || "/assets/img/product/no_product.png"}
                                        alt={item.title || 'Product image'}
                                        onError={(e) => {
                                            e.target.src = "/assets/img/product/no_product.png";
                                        }}
                                    />
                                </a>
                            </div>
                            <div className="tp-product-content-2 pt-15">
                                <div className="tp-product-tag-2">
                                    {item.brandId?.name || 'Brand'}
                                </div>
                                <h3 className="tp-product-title-2">
                                    <a
                                        href={item.linkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={item.title}
                                    >
                                        <span>{item.title}</span>
                                    </a>
                                </h3>
                                <div className="tp-product-rating-icon tp-product-rating-icon-2">
                                    {renderStarRating(item.rating)}
                                </div>
                                <div className="tp-product-price-wrapper-2">
                                    <span className="tp-product-price-2 new-price">
                                        ${item.price?.amount || 'N/A'}
                                    </span>
                                    {item.price?.originalAmount && item.price.originalAmount !== item.price.amount && (
                                        <span className="tp-product-price-2 old-price">
                                            ${item.price.originalAmount}
                                        </span>
                                    )}
                                </div>
                                <div className="tp-product-list-add-to-cart mt-10">
                                    {isLoggedIn ? (
                                        <a
                                            href={item.linkUrl}
                                            className="tp-product-list-add-to-cart-btn"
                                            rel="noopener noreferrer"
                                            aria-label={`Buy ${item.title} now`}
                                        >
                                            Buy Now
                                        </a>
                                    ) : (
                                        <a
                                            href={`${Membershipsignup}/signup`}
                                            className="tp-product-list-add-to-cart-btn"
                                            rel="noopener noreferrer"
                                            aria-label={`Buy ${item.title} now`}
                                        >
                                            Buy Now
                                        </a>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render product list
    const renderProductList = () => {
        if (!loading && hasLoaded && (!product?.products || product.products.length === 0)) {
            return (
                <div className="text-center py-5">
                    <h4>No products found</h4>
                </div>
            );
        }
        return (
            <div className="tp-shop-list-wrapper tp-shop-item-primary mb-70">
                <div className="row tp_products_list">
                    <div className="col-xl-12" >
                        {product.products.filter(item => {
                            const src = (item.cloudFrontImageUrl || item.productLogo || '').trim();
                            return src !== '' && !src.toLowerCase().includes('.tif');
                        }).map((item, index) => (

                            <div className="tp-product-list-item d-md-flex" key={item._id || index}>
                                <div className="tp-product-list-thumb1 p-relative fix">
                                    <a
                                        href={item.linkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`View ${item.title}`}
                                    >
                                        <img
                                            // loading="lazy"
                                            src={item.cloudFrontImageUrl || item.productLogo || "/assets/img/product/no_product.png"}
                                            alt={item.title || 'Product image'}
                                            onError={(e) => {
                                                e.target.src = "/assets/img/product/no_product.png";
                                            }}
                                        />
                                    </a>
                                </div>
                                <div className="tp-product-list-content">
                                    <div className="tp-product-content-2 pt-15">
                                        <div className="tp-product-tag-2">{item.brandId?.name || 'Brand'}</div>
                                        <h3 className="tp-product-title-2">
                                            <a
                                                href={item.linkUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={item.title}
                                            >
                                                <span>{item.title}</span>
                                            </a>
                                        </h3>
                                        <div className="tp-product-rating-icon tp-product-rating-icon-2">
                                            {renderStarRating(item.rating)}
                                        </div>
                                        <div className="tp-product-price-wrapper-2">
                                            <span className="tp-product-price-2 new-price">
                                                ${item.price?.amount || 'N/A'}
                                            </span>
                                            {item.price?.originalAmount && item.price.originalAmount !== item.price.amount && (
                                                <span className="tp-product-price-2 old-price">
                                                    ${item.price.originalAmount}
                                                </span>
                                            )}
                                        </div>
                                        <p
                                            title={sanitizeDescription(item.description).replace(/<[^>]+>/g, '')}
                                            dangerouslySetInnerHTML={{ __html: sanitizeDescription(item.description) }}
                                        ></p>


                                        <div className="tp-product-list-add-to-cart mt-10">
                                            <div className="tp-product-list-add-to-cart mt-10">
                                                {isLoggedIn ? (
                                                    <a
                                                        href={item.linkUrl}
                                                        className="tp-product-list-add-to-cart-btn"
                                                        rel="noopener noreferrer"
                                                        aria-label={`Buy ${item.title} now`}
                                                    >
                                                        Buy Now
                                                    </a>
                                                ) : (
                                                    <a
                                                        href={Membershipsignup}
                                                        className="tp-product-list-add-to-cart-btn"
                                                        rel="noopener noreferrer"
                                                        aria-label={`Buy ${item.title} now`}
                                                    >
                                                        Buy Now
                                                    </a>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };
    const renderCategoryTree = (categories, level = 0) => {
        return (
            <ul className={`filter-items d-flex flex-wrap ${level > 0 ? 'ml-20 mt-10' : ''}`}>
                {categories.map((category, index) => {
                    const isOpen = openSubcategoryIndexes[category.id];
                    const hasChildren = category.children && category.children.length > 0;
                    const dropdownClass = level === 0 ? `catdrpdwn${index}` : level === 1 ? `subcatdrpdwn${index}` : '';
                    const subListClass = level === 0 ? `subcategorylist${index}` : level === 1 ? `childcategorylist${index}` : '';

                    return (
                        <li key={category.id} className="filter-item">
                            <div
                                className={`w-100 d-inline-flex p-relative ${dropdownClass} crsr_pntr`}
                                onClick={() => {
                                    if (hasChildren) {
                                        setOpenSubcategoryIndexes(prev => ({
                                            ...prev,
                                            [category.id]: !prev[category.id],
                                        }));
                                    } else {
                                        handleCategoryToggle(category.id);
                                        setIsFilterOffcanvasOpen(false);
                                    }
                                }}
                            >
                                <div className={`tp-shop-widget-brand-item ${selectedCategoryIds.includes(category.id) ? 'f_helveticabold' : ''}`}>
                                    {category.name}
                                </div>
                                {hasChildren && (
                                    <div className="cat_drpdwn right0">
                                        <i className="fa-solid fa-angle-down"></i>
                                    </div>
                                )}
                            </div>

                            {hasChildren && (
                                <div
                                    className={subListClass}
                                    style={{
                                        maxHeight: isOpen ? '1000px' : '0px',
                                        opacity: isOpen ? 1 : 0,
                                        overflow: 'hidden',
                                        transition: isOpen ? 'all 2.0s ease' : 'all 0.9s ease',
                                    }}
                                >
                                    {renderCategoryTree(category.children, level + 1)}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderCategoryTreeForRightPanel = (categories, level = 0) => {
        return (
            <ul className={`filter-items d-flex flex-wrap ${level > 0 ? 'ml-20 mt-10' : ''}`}>

                {categories.map((category, index) => {
                    const isOpen = openSubcategoryIndexes[category.id];
                    const hasChildren = category.children && category.children.length > 0;

                    return (
                        <li key={category.id} className="filter-item" >
                            <div
                                className="w-100 d-inline-flex p-relative crsr_pntr"
                                onClick={() => {
                                    if (hasChildren) {
                                        setOpenSubcategoryIndexes(prev => ({
                                            ...prev,
                                            [category.id]: !prev[category.id],
                                        }));
                                    } else {
                                        handleCategoryToggle(category.id);
                                        setIsFilterOffcanvasOpen(false);
                                    }
                                }}
                            >
                                <span>{category.name}</span>
                                {hasChildren && (
                                    <div className="cat_drpdwn right0">
                                        <i className="fa-solid fa-angle-down"></i>
                                    </div>
                                )}
                            </div>

                            {hasChildren && isOpen && (
                                <div>
                                    {renderCategoryTreeForRightPanel(category.children, level + 1)}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };


    const handleSeeMoreBrands = () => {
        // if (!showAllBrands) {
        //     fetchBrands(1, selectedCategoryIds, 500);
        // } else {
        //     fetchBrands(1, selectedCategoryIds, 10);
        // }
        setShowAllBrands(prev => !prev);
        setBrandPage(1);
    };


    // Collect all category IDs used in the current product list
    const getUsedCategoryIds = (products) => {
        const ids = new Set();
        products.forEach((p) => {
            if (p.category?._id) ids.add(p.category._id);
            if (Array.isArray(p.categories)) {
                p.categories.forEach((cat) => {
                    if (cat?._id) ids.add(cat._id);
                });
            }
        });
        return ids;
    };

    const filterCategoryTreeByProductIds = (categories, usedIds) => {
        return categories
            .map((cat) => {
                const filteredChildren = filterCategoryTreeByProductIds(cat.children || [], usedIds);
                const includeThis = usedIds.has(cat._id) || filteredChildren.length > 0;
                if (includeThis) {
                    return {
                        ...cat,
                        children: filteredChildren,
                    };
                }
                return null;
            })
            .filter(Boolean);
    };

    const sanitizeDescription = (html) => {

        let cleaned = html
            // Remove completely empty tags (ul, li, p, span, strong, etc.)
            .replace(/<(\w+)[^>]*>\s*<\/\1>/g, '')
            // Remove spans that only contain spaces
            .replace(/<span>\s*<\/span>/g, '')
            // Remove extra whitespace between tags
            .replace(/>\s+</g, '><')
            // Trim spaces at start and end
            .trim();

        return cleaned;
    };


    const usedCategoryIds = getUsedCategoryIds(product.products);
    const filteredCategoriesForRightPanel = filterCategoryTreeByProductIds(categories, usedCategoryIds);



    return (
        <div className='accent-custom'>
            <Header />
            <main>
                {/* breadcrumb area start */}
                <section className="breadcrumb__area include-bg pt-20">
                    <div className="container">
                        <div className="row">
                            <div className="col-xxl-12">
                                <div className="breadcrumb__content p-relative z-index-1">
                                    <div className="breadcrumb__list">
                                        <span><Link to="/">Home</Link></span>
                                        {filterLabel === 'Categories' && <span>Shop</span>}
                                        {filterLabel === 'Brands' && (
                                            <>
                                                <span>Brands</span>
                                                {currentBrandName && <span>{currentBrandName}</span>}
                                            </>
                                        )}
                                    </div>


                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                {/* breadcrumb area end */}
                {/* Brand Slider Section  */}
                {(brandsLoading || brandsFromApi.length > 0) && fromHeaderSearch && (
                    <section className="tp-shop-area pt-10 pb-20">
                        <div className="container">
                            <div className="row align-items-end">
                                <div className="col-xl-5 col-lg-5 col-md-6 col-sm-6 col-6">
                                    <div className="tp-section-title-wrapper mb-10">
                                        <h3 className="tp-section-title">Brands</h3>
                                    </div>
                                </div>
                                <div className="col-xl-7 col-lg-7 col-md-6 col-sm-6 col-6">
                                    <div className="tp-product-arrival-more-wrapper d-flex justify-content-end">
                                        <div className="tp-product-arrival-arrow tp-swiper-arrow mb-20 text-end tp-product-arrival-border">
                                            <button type="button" className="tp-arrival-slider-button-prev">
                                                <i className="fa-solid fa-chevron-left" />
                                            </button>
                                            <button type="button" className="tp-arrival-slider-button-next">
                                                <i className="fa-solid fa-chevron-right" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* brand slide section */}
                            <div className="row">
                                <div className="col-xl-12">
                                    {brandsLoading ? (
                                        <div className="tp-product-arrival-slider fix">
                                            <div className="row">
                                                {Array(4).fill(0).map((_, i) => (
                                                    <div className="col-xl-3 col-md-6 col-sm-6" key={i}>
                                                        <div className="tp-product-item mb-10 transition-3 mb-25 tp_searchresults_proditem">
                                                            <div className="tp-product-thumb p-relative z-index-1 tp_searchresultsadvertisers">
                                                                <Skeleton height={200} />
                                                            </div>
                                                            <div className="tp-product-content">
                                                                <div className="text-center pt-10">
                                                                    <Skeleton height={20} width={120} style={{ margin: '0 auto' }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : brandsFromApi.length > 0 ? (
                                        <div className="tp-product-arrival-slider fix">
                                            <div className="tp-product-arrival-active swiper">
                                                <div className="swiper-wrapper">
                                                    {brandsFromApi.map((brand) => (
                                                        <div key={brand.id || brand._id} className="tp-product-item mb-10 transition-3 mb-25 tp_searchresults_proditem swiper-slide">
                                                            <Link
                                                                to={`/product/${brand.id || brand._id}`}
                                                                state={{ filterLabel: 'Brands' }}
                                                            >
                                                                <div className="tp-product-thumb p-relative z-index-1 tp_searchresultsadvertisers">
                                                                    <img
                                                                        src={brand.brandLogo || brand.logo || '/assets/img/product/no_product.png'}
                                                                        alt={brand.name || 'Brand'}
                                                                        onError={(e) => {
                                                                            e.target.src = '/assets/img/product/no_product.png';
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="tp-product-content">
                                                                    <h3 className="tp-product-title text-center pt-10">
                                                                        <span>{brand.name || 'Brand'}</span>
                                                                    </h3>
                                                                </div>
                                                            </Link>
                                                        </div>
                                                    ))}
                                                </div>
                                                {/* <div className="swiper-pagination"></div> */}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </section>
                )}
                {/* Products Section - Hide when coming from header search */}

                <section className="tp-shop-area pb-120">

                    <div className="container">
                        {!fromHeaderSearch && (
                            <div className="row mb-15 justify-content-center top_products_header">
                                <div className="col-12 col-md-4 col-lg-4 col-xl-2 d-inline-flex justify-content-end">
                                    <div className="tp-shop-top-filter w-100 ms-0">
                                        <button
                                            type="button"
                                            className="tp-filter-btn filter-open-btn tp-btn-blue1 w-100 tp_categorybtn_pdng"
                                            onClick={() => {
                                                setOpenFilterType('labelPanel');
                                                setIsFilterOffcanvasOpen(true);
                                            }}
                                        >
                                            <span style={{ marginRight: '8px' }}>
                                                <i className="fa-solid fa-sliders" />
                                            </span>
                                            {filterLabel === 'Categories' ? 'Category' : 'Brand'}
                                        </button>
                                    </div>
                                </div>
                                <div className="col-12 col-md-8 col-lg-8 col-xl-4">
                                    <div className="tp-header-search-2 mt-15 mt-md-0">
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                if (tempSearchInput.trim().length >= 2) {
                                                    setSearchInput(tempSearchInput);
                                                    setSelectedBrandIds([]);
                                                    setSelectedCategoryIds([]);
                                                    setShowSuggestions(false);
                                                }
                                            }}
                                        >
                                            <input
                                                type="text"
                                                placeholder="Search for Products..."
                                                value={tempSearchInput}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const trimmed = value.trim();
                                                    setTempSearchInput(value);

                                                    if (trimmed.length < 2) {
                                                        setSuggestions({ brands: [], categories: [], products: [] });
                                                        setShowSuggestions(false);
                                                        return;
                                                    }

                                                    fetchSuggestions(trimmed);
                                                }}

                                            />
                                            <button
                                                type="submit"
                                                aria-label="Search"
                                                disabled={tempSearchInput.trim().length < 2}
                                                style={{
                                                    opacity: tempSearchInput.trim().length < 2 ? 0.5 : 1,
                                                    cursor: tempSearchInput.trim().length < 2 ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                <i className="fa-solid fa-magnifying-glass" />
                                            </button>
                                        </form>

                                        <div
                                            ref={suggestionRef}
                                            className="search_suggestion scrollbar_style"
                                            style={{ display: showSuggestions ? 'block' : 'none' }}
                                        >
                                            {suggestions.brands.length > 0 && (
                                                <>
                                                    <h6 className="f_sz15 f_themered">Brands</h6>
                                                    <ul>
                                                        {suggestions.brands.map((brand) => (
                                                            <li key={brand.id}>
                                                                <Link
                                                                    to={`/product/${brand.id}`}
                                                                    state={{ filterLabel: 'Brands' }}
                                                                    onClick={() => {
                                                                        setSearchInput('');
                                                                        setTempSearchInput('');
                                                                        setShowSuggestions(false);
                                                                        setSelectedBrandIds([brand.id]);
                                                                        setSelectedCategoryIds([]);
                                                                        setSelectedCategoryIds([]);
                                                                        setIsFilterOffcanvasOpen(false);
                                                                    }}
                                                                >
                                                                    {brand.name}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}

                                            {suggestions.categories.length > 0 && (
                                                <>
                                                    <h6 className="f_sz15 f_themered mt-5">Categories</h6>
                                                    <ul>
                                                        {suggestions.categories.map((cat) => (
                                                            <li key={cat.id}>
                                                                <div
                                                                    className="crsr_pntr"
                                                                    onClick={() => {
                                                                        navigate('/product', { state: { filterLabel: 'Categories' } });
                                                                        setSelectedCategoryIds([cat.id]);
                                                                        setSelectedBrandIds([]); // clear brand filter
                                                                        setSearchInput('');
                                                                        setTempSearchInput('');
                                                                        setShowSuggestions(false);
                                                                        setIsFilterOffcanvasOpen(false);
                                                                    }}
                                                                >
                                                                    {cat.name}
                                                                </div>



                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}

                                            {suggestions.products.length > 0 && (
                                                <>
                                                    <h6 className="f_sz15 f_themered mt-5">Products</h6>
                                                    <ul>
                                                        {suggestions.products.map((prod) => (
                                                            <li key={prod.id}>
                                                                <Link
                                                                    to="/product"
                                                                    state={{ filterLabel: 'Products', searchTerm: prod.name }}
                                                                    onClick={() => {
                                                                        setSearchInput(prod.name);
                                                                        setTempSearchInput(prod.name);
                                                                        setShowSuggestions(false);
                                                                        setIsFilterOffcanvasOpen(false);
                                                                    }}
                                                                >
                                                                    {prod.name}
                                                                </Link>

                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="row">
                            <div className="col-xl-12 col-lg-12 col-12">
                                <div className="tp-shop-main-wrapper">
                                    <div className="tp-shop-top mb-45">
                                        <div className="row align-items-center">
                                            <div className="col-xl-6">
                                                <div className="tp-shop-top-right d-sm-flex align-items-center">
                                                    <div className="tp-shop-top-tab tp-tab">
                                                        <ul className="nav nav-tabs" role="tablist">
                                                            <li className="nav-item" role="presentation">
                                                                <button
                                                                    className={`nav-link ${activeTab === 'grid' ? 'active' : ''}`}
                                                                    onClick={() => setActiveTab('grid')}
                                                                    type="button"
                                                                    aria-label="Grid view"
                                                                >
                                                                    <i className="fa-solid fa-border-all" />
                                                                </button>
                                                            </li>
                                                            <li className="nav-item" role="presentation">
                                                                <button
                                                                    className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
                                                                    onClick={() => setActiveTab('list')}
                                                                    type="button"
                                                                    aria-label="List view"
                                                                >
                                                                    <i className="fa-solid fa-list-ul" />
                                                                </button>
                                                            </li>

                                                        </ul>
                                                    </div>
                                                </div>

                                            </div>
                                            <div className="col-xl-6">
                                                <div className="tp-shop-top-right d-sm-flex align-items-center justify-content-xl-end">
                                                    <div className="tp-shop-top-select mt-md-0 mt-10">
                                                        <div className="tp-shop-top-select">
                                                            <select
                                                                className="form-select"
                                                                value={sortBy}
                                                                onChange={handleSortChange}
                                                            >
                                                                {sortOptions.map(option => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>


                                                    <div className="tp-shop-top-filter">
                                                        <button
                                                            type="button"
                                                            className="tp-filter-btn cartmini-open-btn"
                                                            onClick={() => {
                                                                setOpenFilterType('offcanvas');
                                                                setIsFilterOffcanvasOpen(true);
                                                            }}
                                                        >
                                                            <span style={{ marginRight: '8px' }}>
                                                                <i className="fa-solid fa-sliders"></i>
                                                            </span>
                                                            Filter
                                                        </button>
                                                    </div>
                                                </div>


                                            </div>
                                            <div className="col-xl-6">

                                            </div>
                                        </div>
                                    </div>
                                    {/* Products Display */}
                                    <div className="tp-shop-items-wrapper tp-shop-item-primary">
                                        <div className="tab-content">
                                            {loading ? (
                                                activeTab === 'grid' ? (
                                                    <div className="row">
                                                        {Array(12).fill(0).map((_, i) => (
                                                            <div className="col-xl-3 col-md-6 col-sm-6" key={i}>
                                                                <Skeleton height={250} />
                                                                <Skeleton height={20} style={{ marginTop: 8 }} />
                                                                <Skeleton height={20} width={100} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="tp-shop-list-wrapper tp-shop-item-primary mb-70">
                                                        <div className="row tp_products_list">

                                                            {Array(6).fill(0).map((_, i) => (
                                                                <div className="col-xl-12 mb-4" key={i}>
                                                                    <div className="tp-product-list-item d-md-flex">
                                                                        <div className="tp-product-list-thumb1 p-relative fix me-4">
                                                                            <Skeleton height={180} width={180} />
                                                                        </div>
                                                                        <div className="tp-product-list-content flex-grow-1">
                                                                            <Skeleton height={24} width="30%" />
                                                                            <Skeleton height={20} width="60%" style={{ marginTop: 10 }} />
                                                                            <Skeleton height={20} width="50%" style={{ marginTop: 10 }} />
                                                                            <Skeleton height={15} width="90%" count={2} style={{ marginTop: 10 }} />
                                                                            <Skeleton height={35} width={120} style={{ marginTop: 20 }} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )
                                            ) : (
                                                <>
                                                    {activeTab === 'grid' && (
                                                        <div className="tab-pane fade show active">
                                                            {renderProductGrid()}
                                                        </div>
                                                    )}
                                                    {activeTab === 'list' && (
                                                        <div className="tab-pane fade show active">
                                                            {renderProductList()}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tp-shop-items-wrapper tp-shop-item-primary">
                            <div className="tab-content">
                                {hasNextPage && !loading && (
                                    <div
                                        ref={loadMoreTriggerRef}
                                        className="d-flex justify-content-center py-4"
                                        style={{ minHeight: '100px' }}
                                    >
                                        {isFetchingMore ? (
                                            <ClipLoader size={30} color="#011879" />
                                        ) : (
                                            <div className="text-muted">
                                                <i className="fa-solid fa-arrow-down"></i>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* activeTab === 'grid' ? (
                                        <div className="row mt-4">
                                            {Array(4).fill(0).map((_, i) => (
                                                <div className="col-xl-3 col-md-6 col-sm-6" key={`skeleton-grid-${i}`}>
                                                    <Skeleton height={250} />
                                                    <Skeleton height={20} style={{ marginTop: 8 }} />
                                                    <Skeleton height={20} width={80} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="tp-shop-list-wrapper tp-shop-item-primary mb-70">
                                            <div className="row tp_products_list">
                                                {Array(2).fill(0).map((_, i) => (
                                                    <div className="col-xl-12 mb-4" key={`skeleton-list-${i}`}>
                                                        <div className="tp-product-list-item d-md-flex">
                                                            <div className="tp-product-list-thumb1 p-relative fix me-4">
                                                                <Skeleton height={180} width={180} />
                                                            </div>
                                                            <div className="tp-product-list-content flex-grow-1">
                                                                <Skeleton height={24} width="30%" />
                                                                <Skeleton height={20} width="60%" style={{ marginTop: 10 }} />
                                                                <Skeleton height={20} width="50%" style={{ marginTop: 10 }} />
                                                                <Skeleton height={15} width="90%" count={2} style={{ marginTop: 10 }} />
                                                                <Skeleton height={35} width={120} style={{ marginTop: 20 }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) */}
                            </div>
                        </div>
                    </div>



                </section>


                {error && (
                    <div className="alert alert-danger text-center py-2">
                        {error}
                    </div>
                )}

                <div className={`cartmini__area left-panel ${isFilterOffcanvasOpen && openFilterType === 'labelPanel' ? 'cartmini__area-opened' : ''}`}>
                    <div className="cartmini__wrapper d-flex justify-content-between flex-column">
                        <div className="cartmini__top-wrapper">
                            <div className="cartmini__top p-relative">
                                <div className="cartmini__top-title"><h4>{filterLabel}</h4></div>
                                <div className="cartmini__close">
                                    <button
                                        type="button"
                                        className="cartmini__close-btn cartmini-close-btn"
                                        onClick={() => setIsFilterOffcanvasOpen(false)}
                                    >
                                        <i className="fa-solid fa-xmark" />
                                    </button>
                                </div>
                            </div>

                            <div className="tp-shop-sidebar">
                                <div className="top_products_filtertype p-relative">
                                    <div>
                                        <div className="tp-shop-widget-categories tp_categories">
                                            <div className="tp-shop-widget-checkbox tp_filter_categories">
                                                {openFilterType === 'labelPanel' && (
                                                    <>
                                                        {filterLabel === 'Categories' && (
                                                            <div>
                                                                <ul className="filter-items d-flex flex-wrap">
                                                                    <li className="filter-item">
                                                                        <div
                                                                            className={`w-100 d-inline-flex p-relative crsr_pntr ${selectedCategoryIds.length === 0 ? 'f_helveticabold' : ''}`}
                                                                            onClick={() => {
                                                                                setSelectedCategoryIds([]);
                                                                            }}
                                                                        >
                                                                            All
                                                                        </div>
                                                                    </li>
                                                                </ul>
                                                                {renderCategoryTree(categories)}
                                                            </div>
                                                        )}


                                                        {filterLabel === 'Brands' && (
                                                            <div className="tp-shop-widget-brand-list">
                                                                <ul className="filter-items d-flex flex-wrap">
                                                                    {visibleBrands.map((brand) => (
                                                                        <li key={brand._id} className="filter-item">
                                                                            <Link
                                                                                to={`/product/${brand._id}`}
                                                                                className={`tp-shop-widget-brand-item crsr_pntr ${selectedBrandIds[0] === brand._id ? 'f_helveticabold' : ''}`}
                                                                                onClick={() => handleSingleBrandSelect(brand._id)}
                                                                            >
                                                                                {brand.name}
                                                                            </Link>
                                                                        </li>
                                                                    ))}
                                                                    {hasMoreBrands && (
                                                                        <li className="filter-item">
                                                                            <div
                                                                                className="prod_filter_seemoreless crsr_pntr"
                                                                                onClick={handleSeeMoreBrands}
                                                                            >
                                                                                {showAllBrands ? 'See Less Brands' : 'See More Brands'}
                                                                            </div>
                                                                        </li>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>


                <div className={`cartmini__area right-panel ${isFilterOffcanvasOpen && openFilterType === 'offcanvas' ? 'cartmini__area-opened' : ''}`}>
                    <div className="cartmini__wrapper d-flex justify-content-between flex-column">
                        <div className="cartmini__top-wrapper">
                            <div className="cartmini__top p-relative">
                                <div className="cartmini__top-title"><h4>Filter</h4></div>
                                <div className="cartmini__close">
                                    <button
                                        type="button"
                                        className="cartmini__close-btn cartmini-close-btn"
                                        onClick={() => setIsFilterOffcanvasOpen(false)}
                                    >
                                        <i className="fa-solid fa-xmark" />
                                    </button>
                                </div>
                            </div>

                            <div className="tp-shop-sidebar">
                                <div className="top_products_filtertype p-relative">
                                    <div>
                                        <div className="tp-shop-widget-categories tp_categories">
                                            <div className="tp-shop-widget-checkbox tp_filter_categories"></div>

                                            {openFilterType === 'offcanvas' && (
                                                <>
                                                    <div className="top_products_filtertype p-relative">
                                                        {/* <div className="top_products_filtertitle prod_brands mb-15 px-0">Brands</div> */}
                                                    </div>
                                                    <div>
                                                        {['Categories'].includes(filterLabel) && (
                                                            <div className="top_products_filtertype p-relative">
                                                                <div className="top_products_filtertitle prod_brands mb-15 px-0">Brands</div>
                                                                <div>
                                                                    <div className="tp-shop-widget-content tp_popularbrands_new">
                                                                        <div className="tp-shop-widget-checkbox">
                                                                            <ul className="filter-items filter-checkbox tp-shop-widget-brand-list d-flex align-items-center justify-content-between flex-wrap">
                                                                                {visibleBrands.map((brand, index) => (
                                                                                    <li key={brand._id} className="filter-item checkbox">
                                                                                        <input
                                                                                            id={`brand-${index}`}
                                                                                            type="checkbox"
                                                                                            checked={selectedBrandIds.includes(brand._id)}
                                                                                            onChange={() => handleBrandToggle(brand._id)}
                                                                                        />
                                                                                        <label htmlFor={`brand-${index}`}>
                                                                                            <div className="tp-shop-widget-brand-item">
                                                                                                <span>{brand.name}</span>
                                                                                            </div>
                                                                                        </label>
                                                                                    </li>
                                                                                ))}
                                                                                {hasMoreBrands && (
                                                                                    <li>
                                                                                        <div
                                                                                            className="prod_filter_seemoreless crsr_pntr"
                                                                                            onClick={handleSeeMoreBrands}
                                                                                        >
                                                                                            {showAllBrands ? 'See Less Brands' : 'See More Brands'}
                                                                                        </div>
                                                                                    </li>
                                                                                )}

                                                                            </ul>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}


                                                    </div>

                                                    {filterLabel === 'Brands' && (
                                                        <div>
                                                            <div className="top_products_filtertitle prod_brands mb-15 px-0">Categories</div>
                                                            <ul className="filter-items d-flex flex-wrap">
                                                                <li className="filter-item">
                                                                    <div
                                                                        className={`tw-100 d-inline-flex p-relative catdrpdwn crsr_pntr ${selectedCategoryIds.length === 0 ? 'f_helveticabold' : ''}`}
                                                                        onClick={() => {
                                                                            setSelectedCategoryIds([]);
                                                                            setIsFilterOffcanvasOpen(false);
                                                                        }}
                                                                    >
                                                                        All
                                                                    </div>
                                                                </li>
                                                            </ul>
                                                            {renderCategoryTreeForRightPanel(filteredCategoriesForRightPanel)}



                                                        </div>
                                                    )}

                                                    <div className="top_products_filtertype p-relative">
                                                        <div className="top_products_filtertitle prod_price mb-15 px-0">Price</div>
                                                        <div>
                                                            <div className="tp-shop-widget-filter">
                                                                <div className="tp-order-info-list-shipping-item d-flex flex-column align-items-start">
                                                                    {priceRanges.map((range, i) => (
                                                                        <div key={i} className="tp-checkout-payment-item">
                                                                            <input
                                                                                type="radio"
                                                                                id={`pricerange${i}`}
                                                                                name="payment"
                                                                                checked={selectedPriceRange === range.value}
                                                                                onChange={() => handlePriceRangeChange(range.value)}
                                                                            />
                                                                            <label htmlFor={`pricerange${i}`}>{range.label}</label>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`body-overlay ${isFilterOffcanvasOpen ? 'body-overlay-open' : ''}`} onClick={() => setIsFilterOffcanvasOpen(false)} />
            </main>
            <Footer />
        </div>
    );
};

export default Product;